"use client";

/**
 * CommandPalette — Cmd+K global search modal.
 * Stub: route navigation + recent items. Wave 28 redesign.
 * Wave 29+: integrate with tRPC global search (products/SKU/keyword).
 */
import * as React from "react";
import { useRouter } from "next/navigation";

const ROUTES: Array<{ href: string; label: string; group: string }> = [
  // Eylem
  { href: "/dashboard", label: "Ana Pano", group: "Eylem" },
  { href: "/inbox", label: "Aksiyon Kutusu", group: "Eylem" },
  { href: "/ask", label: "Sor", group: "Eylem" },
  { href: "/audit", label: "Audit raporu", group: "Eylem" },
  { href: "/investment-allocator", label: "Yatırım Allocator 2×2", group: "Eylem" },
  // Görünürlük
  { href: "/share-of-shelf", label: "Share of Shelf", group: "Görünürlük" },
  { href: "/keywords", label: "Anahtar Kelime Görünürlüğü", group: "Görünürlük" },
  { href: "/sponsored-sov", label: "Sponsorlu SoV", group: "Görünürlük" },
  { href: "/slots", label: "Slot İzleyici", group: "Görünürlük" },
  { href: "/buybox-tracker", label: "BuyBox Pay Tracker", group: "Görünürlük" },
  // Fiyat & Promo
  { href: "/price-tracker", label: "Fiyat Takip", group: "Fiyat & Promo" },
  { href: "/promo-watch", label: "Promo Watch", group: "Fiyat & Promo" },
  { href: "/campaign-pulse", label: "Kampanya Nabzı", group: "Fiyat & Promo" },
  // İçerik
  { href: "/content-quality", label: "İçerik Kalitesi", group: "İçerik" },
  { href: "/image-compliance", label: "Görsel Uyum", group: "İçerik" },
  { href: "/copy-optimizer", label: "Copy Optimizer", group: "İçerik" },
  { href: "/image-gaps", label: "Görsel Eksik Tespiti", group: "İçerik" },
  // Rekabet
  { href: "/velocity", label: "Satış Hızı", group: "Rekabet" },
  { href: "/competitor-scorecard", label: "Rakip Karneleri", group: "Rekabet" },
  { href: "/keyword-gaps", label: "Anahtar Kelime Boşlukları", group: "Rekabet" },
  { href: "/voice-of-shopper", label: "Yorum Sesi", group: "Rekabet" },
  { href: "/forecasting", label: "Tahmin", group: "Rekabet" },
  // Dağıtım
  { href: "/assortment-gap", label: "Ürün Gap", group: "Dağıtım" },
  { href: "/seller-map", label: "Seller Haritası", group: "Dağıtım" },
  { href: "/distribution-coverage", label: "Dağıtım Coverage", group: "Dağıtım" },
  // Settings
  { href: "/scrape-health", label: "Scrape Engine Health", group: "Settings" },
  { href: "/scrape-health", label: "Scrape Engine Health", group: "Settings" },
];

export function CommandPalette(): React.JSX.Element {
  const router = useRouter();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [activeIdx, setActiveIdx] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Open on Cmd+K / Ctrl+K. Wave 31 P0-3 fix: defensive on window+document, skip when typing.
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const k = e.key?.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === "k") {
        e.preventDefault();
        e.stopPropagation();
        setOpen((o) => !o);
        return;
      }
      if (k === "escape") setOpen(false);
    };
    window.addEventListener("keydown", handler, { capture: true });
    return () => window.removeEventListener("keydown", handler, { capture: true } as never);
  }, []);

  React.useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ROUTES;
    return ROUTES.filter((r) => r.label.toLowerCase().includes(q) || r.group.toLowerCase().includes(q));
  }, [query]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && filtered[activeIdx]) {
      e.preventDefault();
      router.push(filtered[activeIdx].href as never);
      setOpen(false);
    }
  };

  if (!open) return <></>;

  const grouped: Record<string, typeof filtered> = {};
  filtered.forEach((r) => { (grouped[r.group] ||= []).push(r); });
  let runningIdx = -1;

  return (
    <div className="cmd-backdrop" onClick={() => setOpen(false)}>
      <div className="cmd" onClick={(e) => e.stopPropagation()}>
        <div className="cmd-search">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: "var(--text-muted)" }}>
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={onKey}
            placeholder="Ara: dashboard, fiyat, eucerin, share of shelf…"
          />
          <span className="kbd">ESC</span>
        </div>
        <div className="cmd-list">
          {filtered.length === 0 ? (
            <div style={{ padding: "32px 16px", textAlign: "center", fontSize: 13, color: "var(--text-muted)" }}>
              Hiç eşleşme yok. Farklı bir terim deneyin.
            </div>
          ) : (
            Object.entries(grouped).map(([group, items]) => (
              <div key={group}>
                <div className="cmd-group-label">{group}</div>
                {items.map((r) => {
                  runningIdx++;
                  const isActive = runningIdx === activeIdx;
                  return (
                    <button
                      key={r.href}
                      type="button"
                      onClick={() => { router.push(r.href as never); setOpen(false); }}
                      onMouseEnter={() => setActiveIdx(runningIdx)}
                      data-active={isActive ? "true" : undefined}
                      className="cmd-item"
                    >
                      <span className="cmd-item-icon">›</span>
                      <span className="cmd-item-label">{r.label}</span>
                      <span className="cmd-item-path">{r.href}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="cmd-foot">
          <span className="cmd-foot-key"><span className="kbd">↑↓</span> gez</span>
          <span className="cmd-foot-key"><span className="kbd">↵</span> aç</span>
          <span className="cmd-foot-key"><span className="kbd">ESC</span> kapat</span>
          <span style={{ marginLeft: "auto" }}>Commerco Cmd+K</span>
        </div>
      </div>
    </div>
  );
}
